import { Namespace, TFunction } from 'react-i18next';
import * as Yup from 'yup';

import { GetAllLeadChannelsResponseTO } from 'models/Leads';
import { Property } from 'models/Properties';
import {
  GetTriggerOptionsResponseTO,
  RecipientType,
  Trigger,
  TriggerActionType,
  TriggerEventType,
  TriggerJobTiming,
  TriggerTimeCondition,
  TriggerType,
} from 'models/automation/Triggers';
import {
  ACTION_TYPE_TO_DEPENDENT_FIELDS_MAPPING,
  ActionTypeFields,
} from '../TriggersTab.constants';
import { getRecipientOptionValue } from '../TriggersTab.utils';
import {
  conditionSchema,
  multiSelectOptionSchema,
} from './TriggerAddEditModal.schema.utils';
import {
  getAvailableTemplates,
  isCopyAgencyRecipientDisabled,
  isCreateJobDisabled,
  isCreateReviewDisabled,
  isDirectMessageDisabled,
  parseConditionsToFormValues,
} from './TriggerAddEditModal.utils';

export const triggerAddEditModalSchema = ({
  t,
}: {
  t: TFunction<Namespace<'en'>>;
}) =>
  Yup.object({
    actionType: Yup.mixed<TriggerActionType | ''>()
      .oneOf(Object.values(TriggerActionType))
      .transform((value) => (value === '' ? null : value))
      .test({
        message: ({ value }: { value: TriggerActionType }) =>
          t('pageAutomations.triggers.actionTypeDisabled', { context: value }),
        name: 'actionTypeDependentValidation',
        test: (actionType, ctx) => {
          switch (actionType) {
            case TriggerActionType.CREATE_JOB:
              return !isCreateJobDisabled(ctx.parent.eventType);
            case TriggerActionType.CREATE_REVIEW:
              return !isCreateReviewDisabled(
                ctx.parent.timeCondition,
                ctx.parent.conditions,
              );
            case TriggerActionType.DIRECT_MESSAGE:
              return !isDirectMessageDisabled(ctx.parent.conditions);
            default:
              return true;
          }
        },
      })
      .required(),
    conditions: Yup.array().of(conditionSchema({ t })),
    eventType: Yup.mixed<TriggerEventType | ''>()
      .oneOf(Object.values(TriggerEventType))
      .transform((value) => (value === '' ? null : value))
      .required(),
    jobTiming: Yup.mixed<TriggerJobTiming | ''>().when('actionType', {
      is: (actionType: TriggerActionType) =>
        ACTION_TYPE_TO_DEPENDENT_FIELDS_MAPPING[actionType]?.includes(
          ActionTypeFields.JOB_TIMING,
        ),
      then: (schema) =>
        schema
          .oneOf(Object.values(TriggerJobTiming))
          .transform((value) => (value === '' ? null : value))
          .required(),
    }),
    name: Yup.string().required(),
    recipientsList: Yup.array()
      .of(multiSelectOptionSchema())
      .when('actionType', {
        is: (actionType: TriggerActionType) =>
          ACTION_TYPE_TO_DEPENDENT_FIELDS_MAPPING[actionType]?.includes(
            ActionTypeFields.RECIPIENTS,
          ),
        then: (schema) =>
          schema
            .min(1, t('form.validation.required'))
            .test({
              message: () =>
                t('pageAutomations.triggers.recipientTypeDisabledTooltip', {
                  context: RecipientType.COPY_AGENCY,
                }),
              name: 'recipientListDisabledOptionValidation',
              test: (recipients) => !isCopyAgencyRecipientDisabled(recipients),
            })
            .required(),
      }),
    serviceUid: Yup.string().when('actionType', {
      is: (actionType: TriggerActionType) =>
        ACTION_TYPE_TO_DEPENDENT_FIELDS_MAPPING[actionType]?.includes(
          ActionTypeFields.SERVICES,
        ),
      then: (schema) => schema.required(),
    }),
    templateUid: Yup.string().when('actionType', {
      is: (actionType: TriggerActionType) =>
        ACTION_TYPE_TO_DEPENDENT_FIELDS_MAPPING[actionType]?.includes(
          ActionTypeFields.TEMPLATES,
        ),
      then: (schema) => schema.required(),
    }),
    timeCondition: Yup.object({
      condition: conditionSchema({ t }),
      type: Yup.mixed<TriggerTimeCondition | ''>()
        .oneOf(Object.values(TriggerTimeCondition))
        .transform((value) => (value === '' ? null : value))
        .required(),
    }).when('triggerType', {
      // If it's not time based, we don't need to validate the timeCondition field
      is: (triggerType: TriggerType) => triggerType !== TriggerType.TIME_BASED,
      then: () => Yup.object(),
    }),
    triggerType: Yup.mixed<TriggerType | ''>()
      .oneOf(Object.values(TriggerType))
      .transform((value) => (value === '' ? null : value))
      .required(),
  });

export type TriggerAddEditModalFormValues = Yup.InferType<
  ReturnType<typeof triggerAddEditModalSchema>
>;

export const getFormDefaultValues = ({
  allLeadChannels,
  allProperties,
  t,
  trigger,
  triggerOptions,
}: {
  allLeadChannels: GetAllLeadChannelsResponseTO['leadChannels'] | undefined;
  allProperties: Property[] | undefined;
  t: TFunction<Namespace<'en'>>;
  trigger?: Trigger | Omit<Trigger, 'uid'>;
  triggerOptions: GetTriggerOptionsResponseTO | undefined;
}): TriggerAddEditModalFormValues => {
  if (!trigger) {
    return {
      actionType: '',
      conditions: [],
      eventType: '',
      jobTiming: '',
      name: '',
      recipientsList: [],
      serviceUid: '',
      templateUid: '',
      timeCondition: {
        condition: {
          conditionType: '',
          all: undefined,
          backupTimeInMilliSeconds: undefined,
          channels: undefined,
          days: undefined,
          daysOfWeek: undefined,
          guests: undefined,
          operator: undefined,
          propertyUids: undefined,
          timeInMilliSeconds: undefined,
          timeType: undefined,
        },
        type: '',
      },
      triggerType: '',
    };
  }

  const recipientOptions = triggerOptions?.option.recipients.map((option) => ({
    ...option,
    id: getRecipientOptionValue({
      recipientUid: option.uid,
      type: option.type,
    }),
  }));

  // Retains only templates available for specific actions
  const availableTemplates = getAvailableTemplates({
    actionType: trigger.actionType,
    triggerOptions,
  });

  const selectedTemplate = availableTemplates.find(
    ({ uid }) => uid === trigger.templateUid,
  );

  return {
    actionType: trigger.actionType,
    eventType: trigger.eventType,
    jobTiming: trigger.jobTiming ?? undefined,
    name: trigger.name,
    recipientsList:
      trigger.recipients?.map((recipient) => {
        const value = getRecipientOptionValue(recipient);
        const label = recipientOptions?.find(({ id }) => id === value)?.name;

        return {
          label: label ?? '',
          value,
          data: recipient,
        };
      }) ?? [],
    serviceUid: trigger.serviceUid ?? '',
    templateUid: selectedTemplate?.uid ?? '',
    triggerType: trigger.triggerType,
    ...parseConditionsToFormValues({
      allLeadChannels,
      allProperties,
      condition: trigger.condition,
      t,
    }),
  };
};
